import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import { invoiceTotalsPanelRecipe } from '../recipes/invoice.recipe';
import { Surface } from './Card';
import { Stack } from './Layout';
import { KeyValueRow } from './List';

export interface InvoiceTotalsPanelProps
  extends React.HTMLAttributes<HTMLDivElement> {
  subtotal: number;
  /**
   * Already resolved to an amount. The label says how it was entered —
   * "Discount (10%)" — and the panel adds the subtotal-after-discount row.
   */
  discount?: { label?: React.ReactNode; amount: number } | null;
  /**
   * The label is a slot because the create screen puts the rate input in it.
   * `null` for the amount means it is not known here — RoadSync works the tax
   * out on its side — and draws a dash.
   */
  tax?: { label?: React.ReactNode; amount: number | null } | null;
  total: number;
}

/**
 * The boxed totals under an invoice's line items.
 *
 *   <InvoiceTotalsPanel
 *     subtotal={1840}
 *     discount={{ label: 'Discount (5%)', amount: 92 }}
 *     tax={{ label: 'Tax (7.5%)', amount: 131.1 }}
 *     total={1879.1}
 *   />
 *
 * A Surface around a divided Stack of KeyValueRows — the shape the create
 * screen and the read-only view were each drawing as a two-column grid with
 * its own padding and its own idea of which row was bold.
 */
export const InvoiceTotalsPanel = React.forwardRef<
  HTMLDivElement,
  InvoiceTotalsPanelProps
>(function InvoiceTotalsPanel(
  { subtotal, discount, tax, total, className, ...props },
  ref
) {
  return (
    <Surface
      ref={ref}
      bordered
      radius="md"
      tone="none"
      className={cn(invoiceTotalsPanelRecipe(), className)}
      {...props}
    >
      <Stack divided>
        <KeyValueRow
          inset="sm"
          label="Subtotal"
          value={subtotal}
          format="currency"
        />
        {discount ? (
          <KeyValueRow
            inset="sm"
            label={discount.label ?? 'Discount'}
            value={-discount.amount}
            format="currency"
            tone="destructive"
          />
        ) : null}
        {discount ? (
          <KeyValueRow
            inset="sm"
            label="Subtotal after discount"
            value={subtotal - discount.amount}
            format="currency"
          />
        ) : null}
        {tax ? (
          tax.amount === null ? (
            <KeyValueRow inset="sm" label={tax.label ?? 'Tax'} value="—" />
          ) : (
            <KeyValueRow
              inset="sm"
              label={tax.label ?? 'Tax'}
              value={tax.amount}
              format="currency"
            />
          )
        ) : null}
        <KeyValueRow
          inset="sm"
          label="Total"
          value={total}
          format="currency"
          emphasis
          highlight
        />
      </Stack>
    </Surface>
  );
});
